// CANONICAL Student Growth Report renderer.
//
// This file is the single source of truth. It is copied verbatim into each
// panel by `Scripts/sync_report_renderers.py`; do not edit the panel copies.
// The same report version must render the same facts for Admin, Teacher,
// Student, and Parent, so any change here must be synced to all four.
import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Loader2, Printer, X } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  confidence,
  date,
  growthScore,
  humanise,
  list,
  number,
  outOf,
  safeName,
  scoreBarWidth,
  text,
  words,
} from "./reportFormat";
import { paginate, partLabel } from "./reportPaging";
import { useViewerInset } from "./useViewerInset";
import "./officialGrowthAchievementPrint.css";

const EVIDENCE_COLUMNS = [
  { label: "Date", render: row => date(row.event_at) },
  {
    label: "Evidence",
    render: row => (
      <>
        <b>{row.title && row.title !== row.sub_metric ? row.title : humanise(row.sub_metric)}</b>
        <small>{words(row.source_type || "evidence")}</small>
      </>
    ),
  },
  { label: "Growth domain", render: row => text(row.domain_label || humanise(row.domain)) },
  { label: "Focus", render: row => humanise(row.sub_metric) },
  { label: "Result", render: row => outOf(row.score, row.maximum, "Awaiting review") },
];

function Metric({ label, value, note }) { return <div className="ga-metric"><span>{label}</span><strong>{value ?? "Not recorded"}</strong><small>{note}</small></div>; }
function Heading({ number: order, title, text: description }) { return <div className="ga-section-heading"><div><span>{order}</span><h2>{title}</h2></div><p>{description}</p></div>; }
function Empty({ children }) { return <p className="ga-empty-copy">{children}</p>; }
function Insights({ title, items, fallback }) { return <section className="ga-insight"><h3>{title}</h3><ul>{(list(items).length ? items : [fallback]).map((item, index) => <li key={index}>{text(item)}</li>)}</ul></section>; }
function RecordTable({ rows, columns, empty }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <table className="ga-table ga-evidence-table">
      <thead><tr>{columns.map(column => <th key={column.label}>{column.label}</th>)}</tr></thead>
      <tbody>{rows.map((row, index) => <tr key={row.source_id || row.id || index}>{columns.map(column => <td key={column.label}>{column.render(row)}</td>)}</tr>)}</tbody>
    </table>
  );
}

export default function OfficialGrowthAchievementDocument({ report, onClose }) {
  const documentRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const { anchorRef, style: insetStyle } = useViewerInset();

  const snapshot = report.source_snapshot || {};
  const growth = snapshot.growth || {};
  const coverage = snapshot.coverage || report.coverage || {};
  const student = report.student_snapshot || report.student || {};
  const schoolName = report.school_branding_snapshot?.school_name || "Edumonics School";
  const domains = Object.values(growth.domains || {}).filter(Boolean);

  // A version issued before the evidence-appendix format carries no
  // `evidence_items` field at all. An empty array means the current format
  // simply found no scored evidence — a data gap, not a historical version.
  const isHistorical = !Array.isArray(growth.evidence_items);

  const items = list(growth.evidence_items).map(item => ({
    ...item,
    source_type: String(item?.source_type || item?.source || "other").toLowerCase().replace(/[\s-]+/g, "_"),
  }));
  const sourceKind = item => item.source_type.includes("quiz") ? "quiz"
    : item.source_type.includes("observ") ? "observation"
    : item.source_type.includes("reflect") ? "reflection" : "other";

  const appendixPages = useMemo(() => {
    const groups = [
      ["Growth quiz evidence", "quiz", "Scored Growth quiz items recorded in this reporting period."],
      ["Observation evidence", "observation", "Rubric-based teacher observations recorded in this reporting period."],
      ["Reflection evidence", "reflection", "Student reflections reviewed and scored in this reporting period."],
      ["Other Growth evidence", "other", "Further scored Growth evidence retained in this snapshot."],
    ];
    const sections = groups
      .map(([heading, kind, description]) => ({
        heading,
        description,
        rows: items.filter(item => sourceKind(item) === kind),
        columns: EVIDENCE_COLUMNS,
        empty: `No ${heading.toLowerCase()} was recorded in this reporting period.`,
      }))
      // A group with no evidence at all is already covered by the profile page.
      .filter(section => section.rows.length);

    // Sections are numbered in one running sequence, continuing from the two
    // overview sections on page 2.
    let section = 2;
    return paginate(sections).map(blocks =>
      blocks.map(block => ({ ...block, number: block.part === 0 ? (section += 1) : section }))
    );
  }, [items]);

  const gaps = list(snapshot.data_gaps);
  const totalPages = 3 + appendixPages.length;

  const downloadPdf = async () => {
    const pdfPages = Array.from(documentRef.current?.querySelectorAll(".ga-pdf-page") || []);
    if (!pdfPages.length || exporting) return;
    setExporting(true);
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
      for (let index = 0; index < pdfPages.length; index += 1) {
        const canvas = await html2canvas(pdfPages[index], { scale: 2.25, useCORS: true, backgroundColor: "#ffffff", logging: false, windowWidth: pdfPages[index].scrollWidth, windowHeight: pdfPages[index].scrollHeight });
        if (index) pdf.addPage("a4", "portrait");
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.96), "JPEG", 0, 0, 210, 297, undefined, "FAST");
      }
      pdf.save(`${safeName(student.name)}_Student_Growth_Report_${safeName(report.academic_year)}_${safeName(report.period_label)}_v${report.version || 1}.pdf`);
    } catch (error) { console.error("Student Growth Report PDF export failed", error); window.alert("The PDF could not be prepared. Please try again."); }
    finally { setExporting(false); }
  };

  const Footer = ({ page }) => <div className="ga-page-footer"><span>Student Growth Report · Version {report.version || 1}</span><span>{String(page).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}</span></div>;

  return (
    <>
      {/* Measures the content column so the viewer never covers the sidebar. */}
      <span ref={anchorRef} aria-hidden="true" />
      {createPortal(
        <div className="official-report-overlay" style={insetStyle}>
          <article className="official-report-paper">
            <div className="official-report-toolbar print:hidden">
              <div className="ga-toolbar-identity">
                {onClose && <button type="button" onClick={onClose} className="ga-toolbar-back" aria-label="Close report"><X size={20} /></button>}
                <div className="ga-toolbar-icon"><FileText size={22} /></div>
                <div><strong>Student Growth Report</strong><span>{student.name || "Student"} · Version {report.version || 1} · {words(report.status || "draft")}</span></div>
              </div>
              <div className="ga-toolbar-actions">
                <button type="button" onClick={() => window.print()} className="ga-secondary-button"><Printer size={16} />Print</button>
                <button type="button" disabled={exporting} onClick={downloadPdf} className="ga-primary-button">{exporting ? <Loader2 size={16} className="ga-spin" /> : <Download size={16} />}{exporting ? "Generating…" : "Export as PDF"}</button>
              </div>
            </div>

            <div ref={documentRef} className="official-report-document">
              <section className="ga-pdf-page ga-page-cover">
                <div className="ga-cover-ribbon"><span>{schoolName}</span><span>Official student report</span></div>
                <div className="ga-cover-centre">
                  <p>Student Growth Report</p>
                  <h1>{student.name || "Student report"}</h1>
                  <div className="ga-cover-rule" />
                  <div className="ga-cover-details">
                    <div><small>Class and section</small><strong>{`Class ${student.grade || "Not recorded"}${student.section ? ` · ${student.section}` : ""}`}</strong></div>
                    <div><small>Reporting period</small><strong>{report.period_label || "Not recorded"}</strong></div>
                    <div><small>Academic year</small><strong>{report.academic_year || "Not recorded"}</strong></div>
                  </div>
                </div>
                <div className="ga-cover-bottom"><span>Growth evidence record</span><span>Issued {date(report.published_at || report.generated_at)}</span></div>
              </section>

              <section className="ga-pdf-page ga-page-profile">
                <div className="ga-page-running"><span>{schoolName}</span><span>Growth profile · {report.period_label}</span></div>
                <Heading number="01" title="Growth overview" text="A verified summary of Growth quizzes, observations, and reflections for this reporting period." />
                <div className="ga-metrics">
                  <Metric label="Growth score" value={growthScore(growth.overall_score)} note={text(growth.overall_level)} />
                  <Metric label="Evidence included" value={number(coverage.growth_evidence ?? items.length, "0")} note="Recorded and scored sources" />
                  <Metric label="Domains recorded" value={number(coverage.domains_recorded ?? domains.length, "0")} note="Seven-domain Growth profile" />
                  <Metric label="Pending review" value={number(coverage.pending_review, "0")} note="Never treated as a score" />
                </div>
                <div className="ga-section-spacer" />
                <Heading number="02" title="Growth profile" text="Domain scores appear only where enough approved evidence is available." />
                {domains.length ? (
                  <div className="ga-domain-grid">
                    {domains.map((domain, index) => (
                      <div className="ga-domain-card" key={domain.key || domain.label || index}>
                        <div><strong>{domain.label || "Growth domain"}</strong><span>{text(domain.level)}</span></div>
                        <div className="ga-score-line"><div className="ga-score-track"><i style={{ width: `${scoreBarWidth(domain.score)}%` }} /></div><b>{growthScore(domain.score)}</b></div>
                        <small>{number(domain.evidence_count, "0")} evidence item(s) · {confidence(domain.confidence)}</small>
                      </div>
                    ))}
                  </div>
                ) : <Empty>No Growth-domain evidence was recorded in this period.</Empty>}
                {isHistorical && <section className="ga-data-note has-gaps"><h3>Historical summary-only version</h3><p>This retained version was issued before the source-evidence appendix format and cannot be expanded. It remains unchanged and valid as issued. To obtain the per-source evidence pages, issue a new Student Growth Report.</p></section>}
                <Footer page={2} />
              </section>

              {appendixPages.map((blocks, pageIndex) => {
                const pageNumber = pageIndex + 3;
                return (
                  <section className="ga-pdf-page ga-evidence-page" key={`evidence-${pageIndex}`}>
                    <div className="ga-page-running"><span>{schoolName}</span><span>Growth evidence · {report.period_label}</span></div>
                    {blocks.map((block, blockIndex) => (
                      <div className="ga-block" key={`${block.heading}-${block.part}-${blockIndex}`}>
                        <Heading
                          number={String(block.number).padStart(2, "0")}
                          title={`${block.heading}${partLabel(block)}`}
                          text={block.description}
                        />
                        <RecordTable rows={block.rows} columns={block.columns} empty={block.empty} />
                      </div>
                    ))}
                    <Footer page={pageNumber} />
                  </section>
                );
              })}

              <section className="ga-pdf-page ga-page-conclusion">
                <div className="ga-page-running"><span>{schoolName}</span><span>Evidence quality and next steps · {report.period_label}</span></div>
                <Heading number="Final" title="Evidence quality and next steps" text="Guidance is limited to the recorded evidence and reviewed school narrative." />
                <div className="ga-evidence-row">
                  <span><b>Sources represented</b>{list(coverage.sources_present).length ? coverage.sources_present.map(humanise).join(", ") : "Not recorded"}</span>
                  <span><b>Latest evidence</b>{date(coverage.latest_evidence_at)}</span>
                  <span><b>Calculation boundary</b>Academic marks and HPC remain separate official reports</span>
                </div>
                <div className="ga-section-spacer" />
                <div className="ga-insight-grid">
                  <Insights title="Strengths observed" items={report.narrative?.strengths} fallback="A reviewed narrative has not been added yet." />
                  <Insights title="Priority for growth" items={report.narrative?.priorities} fallback="Review evidence coverage before identifying a priority." />
                  <Insights title="Student actions" items={report.narrative?.student_actions} fallback="Continue engaging with planned Growth learning activities." />
                  <Insights title="Home support" items={report.narrative?.home_actions} fallback="Discuss the reporting-period learning with the class teacher." />
                </div>
                <section className={`ga-data-note${gaps.length ? " has-gaps" : ""}`}>
                  <h3>{gaps.length ? "Data availability and care" : "Methodology and care"}</h3>
                  {gaps.length ? <ul>{gaps.map((gap, index) => <li key={index}>{text(gap)}</li>)}</ul> : <p>All currently available approved Growth sources were included. This report does not infer missing scores, credits, or academic outcomes.</p>}
                </section>
                <div className="ga-release">
                  <div><small>Generated by</small><strong>{report.generated_by_name || "School reporting system"}</strong></div>
                  <div><small>Publication status</small><strong>{words(report.status || "draft")}</strong></div>
                  <div><small>Issue date</small><strong>{date(report.published_at || report.generated_at)}</strong></div>
                </div>
                <Footer page={totalPages} />
              </section>
            </div>
          </article>
        </div>,
        document.body
      )}
    </>
  );
}
